import { Icon, MenuItem } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ConflictError } from "apis/errors";
import { ActionPermissionsObject, IThemeStorageModel, WorkspaceResourceType, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import UpgradeIcon from "js/Components/UpgradeIcon";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { pluralize } from "js/core/utilities/utilities";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { app } from "js/namespaces";
import { Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { BlueButton, IconMenu, UIPane } from "js/react/components/UiComponents";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { MultiSelectThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import { NoMatchNotice, NoMyResourcesThemeNotice } from "js/react/components/Notice";
import { TeamResourcesPaneHeader } from "js/react/views/TeamResources/components/TeamResourcesPaneHeader";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.TEAMS);

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
    }
`;

const StyledUIPane = styled(UIPane as any)`
    padding: 24px 20px 0;
    display: flex;
    flex-direction: column;
`;

const StyledResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
`;

export function TeamSharedThemesPane(props: WorkspaceControllerState) {
    const { resources } = props;
    const [sharedThemes, setSharedThemes] = useState<IThemeStorageModel[]>([]);
    const [fetching, setFetching] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToCreateSharedResources = (WorkspaceController.plan.tier !== WorkspacePlanTier.BASIC) && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.CREATE);

    useEffect(() => {
        (async () => {
            const sharedThemes = await WorkspaceController.getSharedThemes();
            setSharedThemes(sharedThemes);
            setFetching(false);
        })();
    }, [getObjectHash(resources.filter(resource => resource.resourceType === WorkspaceResourceType.THEME))]);

    const teamThemes: IThemeStorageModel[] = fetching ? [] : sharedThemes.filter(Boolean);
    const filteredThemes = teamThemes.filter(theme => {
        const themeName = theme.get("name")?.toLowerCase() ?? "";
        return !searchTerm || themeName.includes(searchTerm);
    });

    const handleCreate = () => {
        AppController.showThemeEditor({ themeId: "new-team-theme" });
    };

    const handleSearch = (nextSearchTerm: string) => {
        setSearchTerm(nextSearchTerm?.trim().toLowerCase() ?? "");
    };

    const handleEdit = async (theme: IThemeStorageModel) => {
        const teamThemeUsageCount = resources.filter(resource =>
            (resource.resourceType === WorkspaceResourceType.PRESENTATION || resource.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE) &&
            resource.metadata?.sharedThemeId === theme.id
        ).length;
        // Keep dialog language consistent with existing UX by never showing "0 presentations".
        const impactedPresentationCount = Math.max(teamThemeUsageCount, 1);

        const shouldContinue = await ShowConfirmationDialog({
            title: "Editing team theme",
            message: `Any changes you make will impact ${pluralize(
                "presentation",
                impactedPresentationCount,
                "presentations"
            )} in your workspace.`,
            cancelButtonLabel: "Cancel",
            okButtonLabel: "Continue editing",
            optOutProperty: "editTeamThemeDialog"
        });

        if (!shouldContinue) return;

        AppController.showThemeEditor({ themeId: theme.id });
    };

    const handleRename = (theme: IThemeStorageModel) => {
        ShowDialog(InputConfirmationDialog, {
            title: t("Rename Theme"),
            message: null,
            input: {
                value: theme.get("name")
            },
            acceptCallback: async (name: string) => {
                try {
                    await withProgressDialog({
                        title: t("Renaming theme..."),
                        action: async () => {
                            theme.update({ name: name.trim() });
                            await theme.updatePromise;
                        }
                    });
                } catch (err) {
                    logger.error(err, "[TeamSharedThemesPane] Error renaming theme");

                    ShowErrorDialog({
                        title: t("Unable to rename theme"),
                        message: err.message
                    });
                }
            },
        });
    };

    const handleDuplicate = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: t("Duplicating theme..."),
                action: async () => {
                    const name = t("Copy of {{name}}", { name: theme.get("name") });
                    await app.themeManager.createTeamTheme(theme.attributes, name);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error duplicating theme");

            ShowErrorDialog({
                title: t("Unable to duplicate theme"),
                message: err.message
            });
        }
    };

    const handleDelete = async (theme: IThemeStorageModel) => {
        const themeUsageCount = resources.filter(resource =>
            (resource.resourceType === WorkspaceResourceType.PRESENTATION || resource.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE) &&
            resource.metadata?.sharedThemeId === theme.id
        ).length;

        if (themeUsageCount > 0) {
            const confirmed = await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this Team Theme?"),
                message: t("Deleting this Team Theme permanently removes it from your Team Resources. Presentations currently using this theme will keep their existing design, but editors will be able to modify those themes without restriction."),
                okButtonLabel: t("Delete Team Theme"),
                buttonOptions: { acceptButtonColor: "red" }
            });

            if (!confirmed) {
                return;
            }
        }

        try {
            await withProgressDialog({
                title: t("Deleting theme..."),
                action: () => app.themeManager.deleteTeamTheme(theme.id)
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error deleting theme");
            ShowErrorDialog({
                title: t("Unable to delete theme"),
                message: err.message
            });
        }
    };

    const handleToggleHide = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: theme.get("hidden") ? t("Unhiding theme...") : t("Hiding theme..."),
                action: async () => {
                    theme.update({ hidden: !theme.get("hidden") });
                    await theme.updatePromise;
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error toggling hide theme");

            ShowErrorDialog({
                title: t("Unable to toggle hide theme"),
                message: err.message
            });
        }
    };

    return (
        <StyledUIPane>
            {(canManageSharedResources || canUpgradeToManageSharedResources) && (
                <TeamResourcesPaneHeader
                    title={t("Themes")}
                    action={canUseSharedResources && !canUpgradeToCreateSharedResources ? (
                        <BlueButton onClick={handleCreate}>
                            {t("Create Theme")}
                        </BlueButton>
                    ) : null}
                    onSearch={canUseSharedResources ? handleSearch : null}
                    searchPlaceholder={canUseSharedResources ? t("Search themes") : ""}
                />
            )}
            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth padding="0px 0px 20px 0px">
                    <Banner
                        headerText={t("Unlock Team Themes")}
                        bodyText={t("Upgrade your plan to keep colors, fonts, and logos consistent across presentations.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_themes" }
                            }
                        )}
                    />
                </FlexBox>
            )}
            <StyledResultsContainer style={(canUpgradeToManageSharedResources) ? { paddingTop: "20px" } : {}}>
                <FetchingClickShield visible={fetching} backgroundColor="#EEEEEE" />
                {filteredThemes.length > 0 && (
                    <MultiSelectThumbnailGrid
                        responsiveColumns
                        colGap={24}
                        rowGap={24}
                        items={filteredThemes}
                        allowMultiSelect={false}
                        allowDragging={false}
                        readOnly
                        onDoubleClick={(item: IThemeStorageModel) => handleEdit(item)}
                        renderThumbnail={(item: IThemeStorageModel) => {
                            const isDefault = item.id === sharedThemes[0]?.id;
                            return (
                                <ThemeThumbnail
                                    item={item}
                                    isDefault={isDefault && teamThemes.length > 1}
                                    isDowngraded={canUpgradeToManageSharedResources}
                                    menu={
                                        <StyledIconMenu>
                                            {canUpgradeToManageSharedResources ? [
                                                <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Theme...")}</MenuItem>,
                                                <MenuItem key="rename" disabled divider><UpgradeIcon /><Gap5 />{t("Rename")}</MenuItem>,
                                                <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                                                <hr key="divider" />,
                                                item.get("hidden")
                                                    ? <MenuItem key="unhide" disabled divider><UpgradeIcon /><Gap5 />{t("Show")}</MenuItem>
                                                    : <MenuItem key="hide" disabled divider><UpgradeIcon /><Gap5 />{t("Hide")}</MenuItem>,
                                            ].filter(Boolean) : [
                                                <MenuItem key="edit" onClick={() => handleEdit(item)}>
                                                    <Icon>edit</Icon>{t("Edit Theme...")}
                                                </MenuItem>,
                                                <MenuItem key="rename" onClick={() => handleRename(item)}>
                                                    <Icon>label</Icon>{t("Rename")}
                                                </MenuItem>,
                                                <MenuItem key="duplicate" onClick={() => handleDuplicate(item)}>
                                                    <Icon>content_copy</Icon>{t("Duplicate")}
                                                </MenuItem>,
                                                <hr key="divider" />,
                                                item.get("hidden") ? (
                                                    <MenuItem key="unhide" onClick={() => handleToggleHide(item)}>
                                                        <Icon>visibility_on</Icon>{t("Show")}
                                                    </MenuItem>
                                                ) : (
                                                    <MenuItem key="hide" onClick={() => handleToggleHide(item)}>
                                                        <Icon>visibility_off</Icon>{t("Hide")}
                                                    </MenuItem>
                                                )
                                            ].filter(Boolean)}
                                            {!isDefault && (
                                                canUpgradeToManageSharedResources ? (
                                                    <MenuItem key="delete" disabled divider><UpgradeIcon /><Gap5 />{t("Delete...")}</MenuItem>
                                                ) : (
                                                    <MenuItem key="delete" onClick={() => handleDelete(item)}>
                                                        <Icon>delete</Icon>{t("Delete...")}
                                                    </MenuItem>
                                                )
                                            )}
                                        </StyledIconMenu>
                                    }
                                    onDoubleClick={canUpgradeToManageSharedResources ? undefined : (() => handleEdit(item))}
                                />
                            );
                        }}
                    />
                )}
                {teamThemes.length === 0 && <NoMyResourcesThemeNotice />}
                {teamThemes.length > 0 && filteredThemes.length === 0 && <NoMatchNotice />}
            </StyledResultsContainer>
        </StyledUIPane>
    );
}

export default WorkspaceController.withInitializedState(TeamSharedThemesPane);
